import React, { useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import cn from 'classnames';

import useClickOutside from 'utils/useClickOutside';
import useResponsiveQuery from 'utils/useResponsiveQuery';
import ChevronIcon from 'components/common/icons/ChevronIcon';

import css from './styles.module.scss';

export type DropdownId = 'product' | 'analytics' | 'solutions' | undefined;

interface MenuItemType {
  label: string;
  subtitle?: string;
  url: string;
  external?: boolean;
  target?: string;
}

type Props = {
  title: string;
  titleClassName?: string;
  dropdownLinks: MenuItemType[];
  invertColors?: boolean;
  id?: DropdownId;
  activeDropdownId?: string;
  onClick?: React.Dispatch<React.SetStateAction<DropdownId>>;
  colls?: number[]; // each element show how many items in column
  groupLabels?: string[];
  panelDescription?: string;
  onNavigate?: () => void;
};

function sum(arr: number[]) {
  return arr.reduce(function (sum, elem) {
    return sum + elem;
  }, 0);
}

function DropdownMenu({
  title,
  titleClassName,
  dropdownLinks,
  colls,
  groupLabels,
  panelDescription,
  onNavigate,
  ...props
}: Props) {
  const isMobile = useResponsiveQuery(880);
  const isOpen = props.activeDropdownId !== undefined && props.id === props.activeDropdownId;
  const toggleMenu = () => props.onClick?.((id) => (id === props.id ? undefined : props.id));
  const mobileDropdownId = props.id ? `mobile-dropdown-${props.id}` : undefined;
  const { search } = useLocation();

  const menuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(menuRef, () => {
    if (isOpen && !isMobile) toggleMenu();
  });

  const columns = colls
    ? colls.map((col, index) =>
        index === 0
          ? dropdownLinks.slice(0, col)
          : dropdownLinks.slice(sum(colls.slice(0, index)), sum(colls.slice(0, index)) + colls[index]),
      )
    : [dropdownLinks];

  const renderColumns = () =>
    columns.map((items, index) => (
      <div className={css.column} key={`${title}-column-${index}`}>
        {groupLabels?.[index] && <span className={css.groupLabel}>{groupLabels[index]}</span>}
        {items.map((item) => {
          const content = (
            <>
              <span className={css.title}>{item.label}</span>
              {item.subtitle && <span className={css.subtitle}>{item.subtitle}</span>}
              <span className={css.linkArrow} aria-hidden="true">
                {item.external ? '↗' : '→'}
              </span>
            </>
          );

          return item.external ? (
            <a
              key={item.url}
              href={item.url}
              target={item.target || '_blank'}
              rel="noopener noreferrer"
              className={cn(css.megaLink, css.externalLink)}
              onClick={onNavigate}
            >
              {content}
            </a>
          ) : (
            <NavLink
              key={item.url}
              to={`${item.url}${search}`}
              activeClassName={css.navLinkActive}
              className={css.megaLink}
              onClick={onNavigate}
            >
              {content}
            </NavLink>
          );
        })}
      </div>
    ));

  if (isMobile) {
    return (
      <div className={cn(css.dropdownMenu, { [css.isOpen]: isOpen })} ref={menuRef}>
        <button
          type="button"
          className={cn(titleClassName, css.mobileTrigger)}
          aria-expanded={isOpen}
          aria-controls={mobileDropdownId}
          onClick={toggleMenu}
        >
          {title}
          <ChevronIcon className={css.dropdownIcon} />
        </button>
        <div className={css.mobileDropdown} id={mobileDropdownId} aria-hidden={!isOpen}>
          <div className={css.mobileDropdownInner}>
            {columns.map((items, index) => (
              <div className={css.mobileGroup} key={`${title}-mobile-group-${index}`}>
                {groupLabels?.[index] && <span className={css.mobileGroupLabel}>{groupLabels[index]}</span>}
                <div className={css.mobileLinks}>
                  {items.map((item) => {
                    const content = (
                      <>
                        <span className={css.mobileLinkTitle}>{item.label}</span>
                        {item.subtitle && <small className={css.mobileLinkSubtitle}>{item.subtitle}</small>}
                        <span className={css.mobileLinkArrow} aria-hidden="true">
                          {item.external ? '↗' : '→'}
                        </span>
                      </>
                    );

                    return item.external ? (
                      <a
                        key={item.url}
                        href={item.url}
                        target={item.target || '_blank'}
                        rel="noopener noreferrer"
                        className={css.externalLink}
                        onClick={onNavigate}
                      >
                        {content}
                      </a>
                    ) : (
                      <NavLink
                        key={item.url}
                        to={`${item.url}${search}`}
                        activeClassName={css.navLinkActive}
                        onClick={onNavigate}
                      >
                        {content}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // *** Desktop ***
  return (
    <div className={cn(css.menuDesktop, { [css.isOpen]: isOpen })} ref={menuRef}>
      <button
        type="button"
        className={cn(titleClassName, css.desktopTrigger)}
        aria-expanded={isOpen}
        onClick={toggleMenu}
      >
        {title}
        <ChevronIcon className={css.dropdownIcon} />
      </button>
      <div className={css.megaPanel} aria-hidden={!isOpen}>
        <div className={css.megaPanelInner}>
          <div className={css.panelIntro}>
            <span className={css.panelEyebrow}>{title}</span>
            {panelDescription && <p>{panelDescription}</p>}
          </div>
          <div
            className={css.columns}
            style={{ '--menu-columns': columns.length } as React.CSSProperties}
          >
            {renderColumns()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DropdownMenu;
